import React, { useMemo } from 'react'
import styled from 'styled-components'
import { Button } from 'react-bootstrap'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { selectSelectedModelId } from '../../../store/slices/modelSlice'
import { useGetModelTreeQuery, useGetProjectQuery } from '../../../services/projectsApi'
import { useGetModelQuery } from '../../../services/modelsApi'
import ModelTreeVisualization from '../../tree/ModelTreeVisualization'
import { useNavigate } from 'react-router-dom'

const ViewContainer = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
  overflow: hidden;
`

const ContentSection = styled.div`
  flex: 1;
  min-height: 0;
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
`

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: #a0a0a0;
  min-height: 200px;

  h4 {
    margin-bottom: 1rem;
    color: #ffffff;
  }

  p {
    max-width: 400px;
    line-height: 1.5;
  }
`

const CompleteTreeContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
`

const ModelVersionsView: React.FC = () => {
  const navigate = useNavigate()
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId)

  const { data: model } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
  })

  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  const { data: modelTree, isLoading: isTreeLoading } = useGetModelTreeQuery(
    selectedProjectId || '',
    {
      skip: !selectedProjectId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  )

  // Get all parent models (models without a parent)
  const parentModels = useMemo(() => {
    if (!modelTree?.model_tree) return []
    return modelTree.model_tree.filter((node: any) => !node.parent_id)
  }, [modelTree])

  // Create a filtered tree that only includes ancestors and descendants of the selected model
  const filteredTreeData = useMemo(() => {
    if (!modelTree?.model_tree || !selectedModelId) return null

    // Helper to find a node by model ID in the tree
    const findNodeById = (nodes: any[], modelId: string): any | null => {
      for (const node of nodes) {
        if (node.model.id === modelId) return node

        if (node.children && node.children.length > 0) {
          const found = findNodeById(node.children, modelId)
          if (found) return found
        }
      }
      return null
    }

    // Helper to get the path from root to the selected model
    const getPathToModel = (nodes: any[], modelId: string, path: any[] = []): any[] | null => {
      for (const node of nodes) {
        // Try this path
        const newPath = [...path, node]

        // If this is the target, return the path
        if (node.model.id === modelId) return newPath

        // If this node has children, search them
        if (node.children && node.children.length > 0) {
          const foundPath = getPathToModel(node.children, modelId, newPath)
          if (foundPath) return foundPath
        }
      }

      return null
    }

    // Find the selected model node
    const selectedNode = findNodeById(modelTree.model_tree, selectedModelId)
    if (!selectedNode) return null

    // Get the path from root to the selected model
    const pathToSelected = getPathToModel(modelTree.model_tree, selectedModelId)
    if (!pathToSelected) return null

    // Create a completely new filtered tree with only direct ancestors and descendants
    // First, build the path from root to selected model (without any siblings)
    let filteredTree = null
    let currentNode: any = null

    // Work from the top down, creating a clean tree with just the direct path
    for (let i = 0; i < pathToSelected.length; i++) {
      const originalNode = pathToSelected[i]

      // Create a new node with only essential properties
      const newNode: any = {
        model: originalNode.model,
        parent_id: originalNode.parent_id,
        children: [],
      }

      // If this is the first node (root)
      if (i === 0) {
        filteredTree = newNode
        currentNode = newNode
      } else if (currentNode) {
        // Add as a child to the previous node
        currentNode.children = [newNode]
        currentNode = newNode
      }

      // If this is the selected model, preserve all its descendants
      if (originalNode.model.id === selectedModelId && originalNode.children) {
        // Deep clone descendants function
        const cloneDescendants = (node: any): any => {
          if (!node) return null

          const clonedNode = {
            model: node.model,
            parent_id: node.parent_id,
            children: [],
          }

          if (node.children && node.children.length > 0) {
            clonedNode.children = node.children.map((child: any) => cloneDescendants(child))
          }

          return clonedNode
        }

        // Add all descendants of the selected model
        newNode.children = originalNode.children.map((child: any) => cloneDescendants(child))
      }
    }

    return filteredTree
  }, [modelTree, selectedModelId])

  // If there's no model tree data or the model isn't found, show the appropriate message
  if (isTreeLoading) {
    return (
      <ViewContainer>
        <ContentSection>
          <EmptyStateContainer>
            <h4>Loading model tree...</h4>
          </EmptyStateContainer>
        </ContentSection>
      </ViewContainer>
    )
  }

  if (!model) {
    return (
      <ViewContainer>
        <ContentSection>
          <EmptyStateContainer>
            <h4>No Model Selected</h4>
            <p>Please select a model to view its version tree.</p>
            <Button
              variant="primary"
              onClick={() => navigate(`/models/${selectedProjectId}/overview`)}
              style={{
                marginTop: '1rem',
                background: '#9147ff',
                border: 'none',
              }}
            >
              Go to Models Overview
            </Button>
          </EmptyStateContainer>
        </ContentSection>
      </ViewContainer>
    )
  }

  if (!parentModels.length) {
    return (
      <ViewContainer>
        <ContentSection>
          <EmptyStateContainer>
            <h4>No Model Trees Available</h4>
            <p>
              There are currently no model trees in this project. Model trees will appear here once
              you have created models.
            </p>
          </EmptyStateContainer>
        </ContentSection>
      </ViewContainer>
    )
  }

  if (!filteredTreeData) {
    return (
      <ViewContainer>
        <ContentSection>
          <EmptyStateContainer>
            <h4>Model Not Found in Tree</h4>
            <p>The selected model could not be found in the model tree.</p>
          </EmptyStateContainer>
        </ContentSection>
      </ViewContainer>
    )
  }

  return (
    <ViewContainer>
      <ContentSection>
        <CompleteTreeContainer>
          <ModelTreeVisualization
            parent={filteredTreeData}
            project={project}
            highlightModelId={selectedModelId}
            showBackButton={false}
          />
        </CompleteTreeContainer>
      </ContentSection>
    </ViewContainer>
  )
}

export default ModelVersionsView
